import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, FormGrid, ModalError } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { ApiError, czyStarszaInstancja } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { PrzyciskTxt } from '@/components/PrzyciskTxt';
import { WyborKolumn } from '@/components/WyborKolumn';
import { zestawStartowy } from '@/lib/kolumny-raportu';
import { odczytajZestaw, zapamietajZestaw } from '@/lib/pamiec-kolumn';
import { katalogKolumn, pobierzRaport, type FormatPliku, type NazwaRaportu } from '@/lib/raporty-api';

/**
 * WŁASNY RAPORT — wybór kolumn, ich KOLEJNOŚĆ i plik składany na serwerze.
 *
 * ⚠️ OSOBNA DROGA OD PRZYCISKU „EKSPORT" NA LIŚCIE, i to jest celowe. Tamten oddaje TO, CO WIDAĆ
 * na ekranie (razem z PDF-em) i zostaje bez zmian — ktoś, kto pobiera go od pół roku, nie może
 * dostać innego pliku dlatego, że dołożyliśmy funkcję obok. Ten daje zestaw wybrany przez człowieka
 * i jest **tą samą drogą, którą pojedzie wysyłka z harmonogramu** — czyli plik z ekranu i plik
 * z maila powstają z jednego kodu i jednego katalogu kolumn.
 *
 * ⚠️ KOLEJNOŚĆ JEST POŁOWĄ TEJ FUNKCJI. „Wybrane kolumny" to LISTA UPORZĄDKOWANA, nie zbiór
 * zaznaczeń: człowiek prosi o „ilość przed towarem", bo tak wygląda jego arkusz, i plik ma tak
 * wyglądać. Dlatego dwie listy (wybrane u góry, dostępne pod nimi), a nie jedna z checkboxami —
 * w jednej liście nie ma gdzie pokazać porządku.
 */

export function WlasnyRaport({
  raport,
  zadanie,
  onClose,
}: {
  raport: NazwaRaportu;
  /**
   * Filtry raportu — dokładnie te, które ma na sobie ekran (zakres dat, automat, pracownik…).
   *
   * ⚠️ PRZYCHODZĄ Z EKRANU, a nie są wybierane od nowa w tym oknie. Człowiek ustawił już filtry
   * na liście i patrzy na wynik; drugi zestaw filtrów w oknie eksportu znaczyłby, że plik może
   * opisywać coś innego niż to, co widać za oknem.
   */
  zadanie: Record<string, unknown>;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [wybrane, setWybrane] = useState<string[] | null>(null);
  const [blad, setBlad] = useState<string | null>(null);

  const katalog = useQuery({ queryKey: ['raport-kolumny', raport], queryFn: () => katalogKolumn(raport) });

  /**
   * ⚠️ ZESTAW STARTOWY: zapamiętany, a w jego braku DOMYŚLNY Z SERWERA. Nigdy pusty — okno
   * z zerem zaznaczonych kolumn wygląda na zepsute, a nie na „wybierz sobie".
   *
   * ⚠️ ZAPAMIĘTANY ZESTAW PRZESIEWAMY PRZEZ KATALOG. Kolumna mogła zniknąć albo zmienić klucz
   * między wydaniami, a wysłanie nieznanego klucza kończy się odmową serwera — czyli człowiek
   * dostawałby błąd za coś, co zapamiętaliśmy mu sami.
   *
   * ⚠️⚠️ PAMIĘĆ UNIEWAŻNIA SIĘ SAMA PO ZMIANIE DOMYŚLNYCH (`odczytajZestaw` porównuje odcisk).
   * Zgłoszone 31.08.2026: „miałeś przenieść pozycje 3, 7 i 8 jako opcjonalne, a cały czas są
   * wybrane domyślnie" — zmiana była wdrożona, a przebijał ją zapis w przeglądarce sprzed niej.
   */
  useEffect(() => {
    if (katalog.data === undefined || wybrane !== null) return;
    setWybrane(
      zestawStartowy(katalog.data.kolumny, katalog.data.domyslne, odczytajZestaw(raport, katalog.data.domyslne)),
    );
  }, [katalog.data, raport, wybrane]);

  /**
   * ⚠️ ZMIENNA MUTACJI TO OBIEKT, NIE SAM FORMAT, i to nie jest kosmetyka. Przy `txt` jedzie z nią
   * WYBÓR NAGŁÓWKÓW z mini listy (`PrzyciskTxt`); pominięcie pola znaczy „weź ustawienie wyspy",
   * czyli dokładnie to, co robią harmonogramy — patrz `naglowki` w `plik.dto.ts`.
   */
  const pobierz = useMutation({
    mutationFn: ({ format, naglowki }: { format: FormatPliku; naglowki?: boolean }) =>
      pobierzRaport(raport, {
        ...zadanie,
        format,
        kolumny: wybrane ?? [],
        ...(naglowki === undefined ? {} : { naglowki }),
      }),
    onSuccess: (r) => {
      zapamietajZestaw(raport, wybrane ?? [], katalog.data?.domyslne ?? []);
      toast.success(t('rapw.gotowe').replace('{n}', String(r.wierszy)));
      onClose();
    },
    onError: (e: Error) => setBlad(e instanceof ApiError ? e.message : t('common.error')),
  });

  const mozna = (wybrane?.length ?? 0) > 0 && !pobierz.isPending;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('rapw.tytul')} description={t('rapw.opis')}>
        <FormGrid>
          {katalog.isPending && <p className="text-xs text-muted-foreground">{t('common.loading')}</p>}
          {katalog.isError &&
            (czyStarszaInstancja(katalog.error) ? (
              <p className="text-xs">{t('common.starszaInstancja')}</p>
            ) : (
              <p className="text-xs text-destructive">
                {t('common.error')}: {(katalog.error as Error).message}
              </p>
            ))}

          {wybrane !== null && katalog.data !== undefined && (
            <WyborKolumn
              katalog={katalog.data.kolumny}
              domyslne={katalog.data.domyslne}
              wybrane={wybrane}
              onZmiana={setWybrane}
            />
          )}

          <ModalError>{blad}</ModalError>
        </FormGrid>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {/*
            ⚠️ KOLEJNOŚĆ FORMATÓW: CSV, TXT, XLSX — ta sama co w pasku listy (zgłoszone 31.08.2026:
            „trzeba ujednolicić kolejność i nazwy plików"). Dotąd TXT stał przed CSV, a przy liście
            napis brzmiał „Excel" — dwa paski z tymi samymi formatami inaczej ułożone i inaczej
            nazwane to najprostszy sposób, żeby ktoś kliknął nie ten przycisk.

            ⚠️ PDF-A TU NIE MA i to nie jest przeoczenie: składa go `pdfmake` w przeglądarce,
            a ten raport powstaje na serwerze — po to, żeby był identyczny z tym, co pojedzie mailem.
            PDF zostaje na pasku przy liście, gdzie plik składa się z kolumn EKRANU.
          */}
          <Button type="button" variant="outline" disabled={!mozna} onClick={() => pobierz.mutate({ format: 'csv' })}>
            {pobierz.isPending && pobierz.variables?.format === 'csv' ? (
              <Loader2 className="animate-spin" />
            ) : (
              <FileText className="size-4" />
            )}
            CSV
          </Button>
          {/*
            ⚠️⚠️ TXT JEST TU, BO O TO OKNO SZŁO OD POCZĄTKU. Zgłoszenie brzmiało „generowanie plików
            .txt tab delimited DLA WYBRANYCH KOLUMN", a pierwsza wersja dołożyła przycisk wyłącznie
            przy liście — tam plik składa się z kolumn EKRANU, nie z wybranych. Zgłoszone jako
            „pobrany txt tego nie miał, chociaż w kolumnach jest zaznaczone".

            ⚠️ LISTA ROZWIJA SIĘ DO GÓRY (`wGore`): stopka modala jest przyklejona do dołu
            przewijanego okna, więc lista otwarta w dół zostaje ucięta przez `overflow-y-auto`.
          */}
          <PrzyciskTxt
            size="default"
            wGore
            disabled={!mozna}
            pracuje={pobierz.isPending && pobierz.variables?.format === 'txt'}
            onWybor={(naglowki) => pobierz.mutate({ format: 'txt', naglowki })}
          />
          <Button type="button" disabled={!mozna} onClick={() => pobierz.mutate({ format: 'xlsx' })}>
            {pobierz.isPending && pobierz.variables?.format === 'xlsx' ? (
              <Loader2 className="animate-spin" />
            ) : (
              <FileSpreadsheet className="size-4" />
            )}
            XLSX
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
